import React, { useEffect, useState } from 'react';
import DialogModal from '../../../components/DialogModal';
import {
  DeclarationItem,
  DropdownOption,
  SchoolDeclarationType
} from '../types';
import {
  getDeclarationHtml,
  updateDeclarationItem
} from '../../../apis/declarationSetup';
import EditDeclarationTable from '../DeclarationTables/EditDeclarationTable';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import DeclarationModal from './DeclarationModal';
import { StyledButton } from '../styled';
import { CircularProgress } from '@mui/material';
import ViewDeclarationFileDialog from './ViewDeclarationFileDialog';
import { isEmpty, isEqual } from 'lodash';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { SnackbarSeverityType } from '../../../enums';
import CloneDeclarationsModal from './CloneDeclarationsModal';

export interface EditDeclarationModalProps {
  open: boolean;
  onClose: () => void;
  selectedDeclaration: SchoolDeclarationType | null;
  gradeOptions: DropdownOption[];
  fetchData: () => Promise<void>;
  showToast: (
    message: string,
    snackbarSeverityType: SnackbarSeverityType
  ) => void;
  onApiSuccessAndFailure: (isSuccess: boolean, message: string) => void;
  declarationSetupData: SchoolDeclarationType[];
  setDeclarationSetupData: React.Dispatch<
    React.SetStateAction<SchoolDeclarationType[]>
  >;
}

const EditDeclarationForGradeModal = ({
  open,
  onClose,
  selectedDeclaration,
  gradeOptions,
  declarationSetupData,
  setDeclarationSetupData,
  fetchData,
  showToast,
  onApiSuccessAndFailure
}: EditDeclarationModalProps) => {
  const [editedDeclaration, setEditedDeclaration] =
    useState<SchoolDeclarationType | null>(null);
  const [originalDeclaration, setOriginalDeclaration] =
    useState<SchoolDeclarationType | null>(null);

  useEffect(() => {
    if (selectedDeclaration) {
      setEditedDeclaration(selectedDeclaration);
      setOriginalDeclaration(selectedDeclaration);
    }
  }, [selectedDeclaration]);
  const isChanged = !isEqual(originalDeclaration, editedDeclaration);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAddDeclarationModalOpen, setIsAddDeclarationModalOpen] =
    useState<boolean>(false);
  const [isEditDeclarationModalOpen, setIsEditDeclarationModalOpen] =
    useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<DeclarationItem | null>(null);
  const [isDeclarationDeleteModalOpen, setIsDeclarationDeleteModalOpen] =
    useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [declarationTitle, setDeclarationTitle] = useState<string>('');
  const [isViewFileDialogOpen, setIsViewFileDialogOpen] =
    useState<boolean>(false);
  const [fileUrlToView, setFileUrlToView] = useState<string | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState<boolean>(false);
  const [selectedDeclarationItem, setSelectedDeclarationItem] =
    useState<DeclarationItem | null>(null);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const declarations = editedDeclaration?.declarations
    ? [...editedDeclaration.declarations].sort(
        (a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0)
      )
    : [];

  const handleCloneSuccess = (message: string) => {
    setIsCloneModalOpen(false);
    fetchData();
    onClose();
    showToast(message, SnackbarSeverityType.success);
  };

  const handleCloneFailure = (message: string) => {
    showToast(message, SnackbarSeverityType.error);
  };

  const handleAddDeclarationModalCancel = () => {
    setIsAddDeclarationModalOpen(false);
  };

  const handleEditDeclarationOpen = (item: DeclarationItem) => {
    setEditingItem(item);
    setIsEditDeclarationModalOpen(true);
  };

  const handleDeleteDeclarationCancel = () => {
    setIsDeleting(false);
    setIsDeclarationDeleteModalOpen(false);
    setSelectedDeclarationItem(null);
  };
  const handleDeleteDeclaration = (declarationItem: DeclarationItem) => {
    setIsDeleting(false);
    setSelectedDeclarationItem(declarationItem);
    setIsDeclarationDeleteModalOpen(true);
  };

  const deleteDeclarationData = (
    DeclarationData: SchoolDeclarationType[],
    declarationItemId: string | null
  ) => {
    const updatedDeclarationData = DeclarationData.map((item) => {
      if (item.id === selectedDeclaration?.id) {
        return {
          ...item,
          declarations: item.declarations.filter(
            (d) => d.id !== declarationItemId
          )
        };
      }
      return item;
    });

    setDeclarationSetupData(updatedDeclarationData);
    const updatedSelected = updatedDeclarationData.find(
      (item) => item.id === selectedDeclaration?.id
    );

    return updatedSelected;
  };

  const handleDeleteDeclarationConfirm = async () => {
    if (isEmpty(selectedDeclarationItem) || !selectedDeclarationItem?.id) {
      showToast(
        'No declaration selected for deletion.',
        SnackbarSeverityType.error
      );
      handleDeleteDeclarationCancel();
      return;
    }
    const updatedSelected = deleteDeclarationData(
      declarationSetupData,
      selectedDeclarationItem?.id
    );

    if (updatedSelected) {
      setEditedDeclaration(updatedSelected);
      handleDeleteDeclarationCancel();
    }
  };

  const handleLabelClick = async (declarationItem: DeclarationItem) => {
    if (!editedDeclaration) return;

    setIsProcessingFile(true);
    setIsViewFileDialogOpen(true);
    setFileUrlToView(null);
    setDeclarationTitle(declarationItem.label);
    try {
      const response = await getDeclarationHtml({
        school: editedDeclaration.school,
        academicYear: editedDeclaration.academicYear,
        templateFileName:
          declarationItem.textTemplateFileName.split('/').at(-1) || ''
      });

      setFileUrlToView(response.data);
    } catch (error) {
      onApiSuccessAndFailure(false, 'Failed to fetch declaration content');
      setIsViewFileDialogOpen(false);
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleEditDeclarationSave = (updatedItem: DeclarationItem) => {
    if (!editedDeclaration) return;

    const updatedDeclarations = (editedDeclaration.declarations || []).map(
      (d) => (d.id === updatedItem.id ? updatedItem : d)
    );

    setEditedDeclaration((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        declarations: updatedDeclarations
      };
    });
  };

  const updateSelectedGrade = async () => {
    if (!editedDeclaration) return;

    const sortedDeclarations = (editedDeclaration.declarations || [])
      .slice()
      .sort((a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0));
    const payload: SchoolDeclarationType = {
      school: editedDeclaration.school || loggedInUser.school || '',
      academicYear: editedDeclaration.academicYear || '',
      grade: editedDeclaration.grade || '',
      declarations: sortedDeclarations || [],
      id: editedDeclaration.id
    };

    setIsSubmitting(true);
    try {
      const response = await updateDeclarationItem(payload);
      if (response) {
        await fetchData();
        onApiSuccessAndFailure(true, 'Declaration updated successfully');
      }
      setEditedDeclaration(response as any);
      setOriginalDeclaration(response as any);
    } catch (error: any) {
      onApiSuccessAndFailure(
        false,
        error?.errors?.[0]?.message || 'Failed to update declaration'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogModal
        maxWidth='md'
        open={open}
        onClose={onClose}
        title={`Declarations for Grade ${editedDeclaration?.grade} (${editedDeclaration?.academicYear})`}
        actions={
          isChanged ? (
            <StyledButton onClick={updateSelectedGrade} disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Update'
              )}
            </StyledButton>
          ) : null
        }
      >
        <EditDeclarationTable
          declarations={declarations}
          onEdit={handleEditDeclarationOpen}
          onDelete={handleDeleteDeclaration}
          onAddDeclarationItem={setIsAddDeclarationModalOpen}
          onCloneDeclarations={() => setIsCloneModalOpen(true)}
          onLabelClick={handleLabelClick}
        />
      </DialogModal>

      {selectedDeclaration && (
        <CloneDeclarationsModal
          open={isCloneModalOpen}
          onClose={() => setIsCloneModalOpen(false)}
          gradeToCloneTo={selectedDeclaration.grade}
          academicYearToCloneTo={selectedDeclaration.academicYear}
          gradeOptions={gradeOptions}
          onApiSuccess={handleCloneSuccess}
          onApiFailure={handleCloneFailure}
        />
      )}

      <ViewDeclarationFileDialog
        open={isViewFileDialogOpen}
        onClose={() => {
          setIsViewFileDialogOpen(false);
          setFileUrlToView(null);
        }}
        fileUrl={fileUrlToView}
        isLoading={isProcessingFile}
        title={declarationTitle}
      />
      <DeclarationModal
        mode='add'
        open={isAddDeclarationModalOpen}
        onClose={handleAddDeclarationModalCancel}
        selectedDeclaration={editedDeclaration}
        setSelectedDeclaration={setEditedDeclaration}
        onApiSuccessAndFailure={onApiSuccessAndFailure}
      />

      <DeclarationModal
        mode='edit'
        open={isEditDeclarationModalOpen}
        onClose={() => setIsEditDeclarationModalOpen(false)}
        onSave={handleEditDeclarationSave}
        declaration={editingItem}
        selectedDeclaration={editedDeclaration}
        onApiSuccessAndFailure={onApiSuccessAndFailure}
      />

      <ConfirmationDialog
        open={isDeclarationDeleteModalOpen}
        onClose={handleDeleteDeclarationCancel}
        message={`Are you sure you want to delete the selected declaration?`}
        onConfirm={handleDeleteDeclarationConfirm}
        disable={isDeleting}
      />
    </>
  );
};

export default EditDeclarationForGradeModal;
